// eslint-disable-next-line @typescript-eslint/no-restricted-imports -- Not messing with useState wrapper in shared/
import { useEffect, useRef, useState } from "react";
import type { HighMemoryInfoV2 } from "owa-client-native-interop/lib/instanceIframeTrackerInterop";
import type { IframeState } from "./useIframeState";
import type { IframeScenario } from "../settings/iframes-mapping";
import { canCallAddOnHighMemoryEventV2EventHandler, addOnHighMemoryEventV2EventHandler, canCallRemoveOnHighMemoryEventV2EventHandler, removeOnHighMemoryEventV2EventHandler, } from "owa-client-native-interop/lib/instanceIframeTrackerInterop";
import { logUsage } from "owa-analytics";
import { getGlobalSettingsAccountMailboxInfo } from "owa-account-source-list-store";
import { trace } from "owa-trace";
import { registerIframeMemoryListener, unregisterIframeMemoryListener, } from "../utils/registerIframeMemoryListener";
import { getSettings } from "../utils/getSettings";
import { useId } from "@fluentui/react-components";
import { setIframeInfo } from "../utils/getIframeInfo";
import { addIframeMetric } from "../metrics/iframeMetrics";
import { checkIfMemoryIsAboveThreshold, computeMemoryFromFrameInfos, convertHighMemoryInfoToFrameInfo, getIframeFullInfo, getIframeMemoryInfo, updateCacheFromFrameInfos, type IframeMemoryInfo, } from "../utils/iframeMemoryService";

export type MemoryNotification = "MEMORY_TOO_HIGH_EVENT" | "SYSTEM_MEMORY_PRESSURE";

type EvictionReasons = "MEMORY_TOO_HIGH_INTERVAL" | MemoryNotification;

const MAX_HIGH_MEMORY_EVENT_COUNT = 20;
let highMemoryEventCount = 0;

/**
 * Iframe Memory and Inactivity Eviction Heuristics
 * - Only apply when cached and in background (inactive).
 * - After close (isVisible == false), evict from cache if:
 *   - Average memory while active was above threshold AND
 *   - The memory is still above the threshold after the window check
 * - While in the background, evict if:
 *   - Memory usage exceeds threshold for two consecutive checks
 *      - This is to avoid spikes that are short-lived
 *   - Inactivity timeout is reached
 */
export function useIframeMemoryManagement(state: IframeState, scenario: IframeScenario, // Should not change
isVisible: boolean, cachingEnabled: boolean, // Should not change
iframesCount?: number, onEvictFromCacheCallback?: (processId: number | undefined, sessionIds: string[] | undefined, currentMemoryUsageInMb?: number, currentFreeMemoryInMb?: number) => void, onHighMemoryEventCallback?: (notificationType: MemoryNotification, processId: number, sessionIds: string[], memoryUsageInMb: number | undefined) => void): {
    doNotRenderCachedIframe: boolean;
} {
    // Unique ID for each listener
    const listenerId = useId(`iframe-${scenario}`);
    
    // This is the only value that should cause a re-render in the component
    const [disableCache, setDisableCache] = useState<boolean>(!cachingEnabled);
    
    // Refs for eviction logic
    const evictOnNextCheck = useRef<boolean>(false);
    const isHidden = useRef<boolean>(!isVisible);
    const evictOnNextVisibilityChange = useRef<EvictionReasons | null>(null);
    const evictionGracePeriodCount = useRef<number>(0);
    
    // Refs for telemetry purposes
    const lastVisibilityChange = useRef<number>(performance.now());
    const totalMemoryUsedInWindow = useRef<number>(0);
    const totalMeasurementInWindow = useRef<number>(0);
    const visibilityChangeCount = useRef<number>(0);
    const numberOfRenderedIframes = useRef<number>(0);
    
    const currentVisibilityDuration = Math.floor((performance.now() - lastVisibilityChange.current) / 1000);
    if (isVisible !== isHidden.current) {
        isHidden.current = !isVisible;
    }
    
    numberOfRenderedIframes.current = iframesCount ?? -1;
    
    // Log the memory of the iframes when the visibility changes
    useEffect(() => {
        const isCacheEnabled = cachingEnabled && !disableCache;
        
        // If we got a SYSTEM_MEMORY_PRESSURE event, evict the iframe
        if (isCacheEnabled && evictOnNextVisibilityChange.current === "SYSTEM_MEMORY_PRESSURE") {
            // Evict the iframe from cache
            onIframeEvictionFromCache("SYSTEM_MEMORY_PRESSURE", scenario, cachingEnabled, -1, numberOfRenderedIframes.current);
            
            if (onEvictFromCacheCallback) {
                onEvictFromCacheCallback(undefined /* processId */, undefined /* sessionIds */, undefined /* currentMemoryUsageInMb */, 0 /* currentFreeMemoryInMb */);
            }
            else {
                // Update the state to force the iframe to re-render
                // eslint-disable-next-line owa-custom-rules/forbid-react-set-state-and-mutators-inside-specific-hooks -- This is need to force the iframe to be evicted. It will only happen once in a session.
                setDisableCache(true);
            }
            // Disable the eviction on next step
            evictOnNextVisibilityChange.current = null;
        }
        
        // Log a visibility change event event
        getIframeMemoryInfo(scenario)
            .then((iframeMemoryInfo: IframeMemoryInfo | undefined) => {
            const memory = iframeMemoryInfo?.totalMemoryMb;
            
            const info = {
                ...getIframeFullInfo(scenario),
                scenario,
                previousVisibility: !isVisible,
                newVisibility: isVisible,
                cachingEnabled,
                currentVisibilityDuration,
                memory: memory ?? -1,
                avgMemoryUsed: Math.floor(totalMemoryUsedInWindow.current / totalMeasurementInWindow.current) || -1,
                visibilityChangeCount: visibilityChangeCount.current,
                numberOfIframes: numberOfRenderedIframes.current,
            };
            
            // Using getGlobalSettingsAccountMailboxInfo: Iframe memory management tracks app-level
            // performance metrics. The iframe wrapper doesn't have account context as it hosts
            // various content that may span multiple accounts.
            logUsage("IframeVisibilityChange", getGlobalSettingsAccountMailboxInfo(), info);
            trace.info(`[DATAPOINT] IframeVisibilityChange - Iframe visibility change: ${JSON.stringify(info)}`, "iframeWrapper");
            
            // If we got a MEMORY_TOO_HIGH_EVENT and we are hidden, check the memory and evict the iframe if needed
            if (isHidden.current &&
                isCacheEnabled &&
                evictOnNextVisibilityChange.current === "MEMORY_TOO_HIGH_EVENT" &&
                memory &&
                checkIfMemoryIsAboveThreshold(memory, state)) {
                // Evict the iframe from cache
                onIframeEvictionFromCache("MEMORY_TOO_HIGH_EVENT", scenario, cachingEnabled, memory, numberOfRenderedIframes.current);
                if (onEvictFromCacheCallback) {
                    onEvictFromCacheCallback(undefined /* processId */, undefined /* sessionIds */, memory, 0 /* currentFreeMemoryInMb */);
                }
                else {
                    // Update the state to force the iframe to re-render
                    setDisableCache(true);
                }
            }
            
            // Disable the eviction on next step
            evictOnNextVisibilityChange.current = null;
        })
            .finally(() => {
            // Reset the tracking values after logging the visibility change
            lastVisibilityChange.current = performance.now();
            visibilityChangeCount.current += 1;
            totalMemoryUsedInWindow.current = 0;
            totalMeasurementInWindow.current = 0;
        });
    }, [isVisible, disableCache, onEvictFromCacheCallback]);
    
    // Check memory health at regular intervals
    useEffect(() => {
        registerIframeMemoryListener(listenerId, scenario, state, (memory, isAboveThreshold) => {
            // Update the total memory used and measurement count
            totalMemoryUsedInWindow.current += memory || 0;
            totalMeasurementInWindow.current += 1;
            trace.info(` Iframe (${scenario}) memory usage: ${memory}, isAboveThreshold: ${isAboveThreshold}`, "iframeWrapper");
            
            // Update the iframe memory metrics
            const metricVisibility = state === "VISIBLE" ? "Visible" : "Hidden";
            addIframeMetric("global", metricVisibility, memory ?? -1);
            if (scenario === "BizChatSidebar") {
                addIframeMetric("copilot", metricVisibility, memory ?? -1);
            }
            
            // If hidden and the iframe is above the threshold,
            if (cachingEnabled && !disableCache && isHidden.current && isAboveThreshold) {
                evictionGracePeriodCount.current += 1;
                
                // Eject if the memory is above the threshold for the grace period count
                if (evictionGracePeriodCount.current >= getSettings().evictionGraceCount) {
                    // Evict the iframe from cache
                    onIframeEvictionFromCache("MEMORY_TOO_HIGH_INTERVAL", scenario, cachingEnabled, memory, numberOfRenderedIframes.current);
                    if (onEvictFromCacheCallback) {
                        onEvictFromCacheCallback(undefined /* processId */, undefined /* sessionIds */, memory, undefined /* currentFreeMemoryInMb */);
                    }
                    else {
                        // Update the state to force the iframe to re-render
                        // eslint-disable-next-line owa-custom-rules/forbid-react-set-state-and-mutators-inside-specific-hooks -- This is need to force the iframe to be evicted. It will only happen once in a session.
                        setDisableCache(true);
                    }
                    
                    // Disable the eviction on next step
                    evictOnNextCheck.current = false;
                    evictionGracePeriodCount.current = 0;
                    return;
                }
            }
        });
        
        return () => unregisterIframeMemoryListener(listenerId);
    }, [disableCache, state, onEvictFromCacheCallback]);
    
    // Listen for high memory events from the native host
    useEffect(() => {
        const highMemoryListener = async (events: HighMemoryInfoV2[]) => {
            const frameInfos = events.map(convertHighMemoryInfoToFrameInfo);
            updateCacheFromFrameInfos(frameInfos);
            
            const memoryInfo = computeMemoryFromFrameInfos(scenario, frameInfos);
            if (!memoryInfo) {
                return;
            }
            
            const pressureRatio = events[0].systemMemoryPressureRatio; // all events have the same system memory pressure ratio
            const totalScenarioMemoryMb = memoryInfo.totalMemoryMb;
            
            const evictionReason = checkForEviction(totalScenarioMemoryMb, pressureRatio, state);
            if (evictionReason) {
                evictOnNextVisibilityChange.current = evictionReason;
            }
            
            if (evictOnNextVisibilityChange.current) {
                setIframeInfo(scenario, "markedForEviction", true);
                evictionGracePeriodCount.current += 1;
                const info = {
                    ...getIframeFullInfo(scenario),
                    reason: evictOnNextVisibilityChange.current,
                    scenario,
                    cachingEnabled,
                    memory: totalScenarioMemoryMb,
                    pressureRatio,
                    numberOfIframes: numberOfRenderedIframes.current,
                };
                // This could be noisy in a bad session
                if (highMemoryEventCount++ <= MAX_HIGH_MEMORY_EVENT_COUNT) {
                    logUsage("IframeNativeHostHightMemoryEvent", getGlobalSettingsAccountMailboxInfo(), info);
                }
                
                trace.info(`[DATAPOINT] IframeNativeHostHightMemoryEvent - Iframe marked for eviction: ${JSON.stringify(info)}`, "iframeWrapper");
            }
            else {
                // Reset the grace period count if the memory is back to normal
                evictionGracePeriodCount.current = 0;
            }
            
            // TODO: this entire fn should be updated to include per-process eviction, but not yet implemented
            for (const [pid, processInfo] of memoryInfo.processes) {
                const processMemoryMb = processInfo.memoryMb;
                const eventType = checkForEviction(processMemoryMb, pressureRatio, state);
                if (eventType) {
                    onHighMemoryEventCallback?.(eventType, pid, processInfo.iframes.map(f => f.correlationId), processMemoryMb);
                }
            }
        };
        
        const registrationPromise = (async () => {
            if (await canCallAddOnHighMemoryEventV2EventHandler()) {
                return await addOnHighMemoryEventV2EventHandler(highMemoryListener);
            }
            return undefined;
        })();
        
        return () => {
            registrationPromise.then(async (token) => {
                if (token && (await canCallRemoveOnHighMemoryEventV2EventHandler())) {
                    await removeOnHighMemoryEventV2EventHandler(token);
                }
            });
        };
    }, [state, onHighMemoryEventCallback]);
    
    return {
        doNotRenderCachedIframe: disableCache && !isVisible,
    };
}

function onIframeEvictionFromCache(reason: EvictionReasons, scenario: IframeScenario, cachingEnabled: boolean, memory: number | undefined, numberOfIframes: number) {
    setIframeInfo(scenario, "evictedFromCache", true);
    const info = {
        ...getIframeFullInfo(scenario),
        reason,
        scenario,
        cachingEnabled,
        memory,
        numberOfIframes,
    };
    
    // Using getGlobalSettingsAccountMailboxInfo: Iframe memory management tracks app-level
    // performance metrics, not tied to any specific account's data operations.
    logUsage("IframeEvictedFromCache", getGlobalSettingsAccountMailboxInfo(), info);
    trace.warn(`[DATAPOINT] IframeEvictedFromCache: ${JSON.stringify(info)}`, "iframeWrapper");
}

function checkForEviction(memoryInMb: number | undefined, pressureRatio: number | undefined, state: IframeState): MemoryNotification | null {
    if (memoryInMb && checkIfMemoryIsAboveThreshold(memoryInMb, state)) {
        return "MEMORY_TOO_HIGH_EVENT";
    }
    else if (pressureRatio && pressureRatio > getSettings().memoryPressureThreshold) {
        return "SYSTEM_MEMORY_PRESSURE";
    }
    return null;
}
